import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export function AuthModal() {
  const { authOpen, setAuthOpen, authMode } = useUI();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(authMode);
  const [form, setForm] = useState({ display_name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authOpen) {
      setMode(authMode);
      setErr("");
    }
  }, [authOpen, authMode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.display_name || "Hunter");
      toast.success(mode === "login" ? "Welcome back." : "Welcome to HomeRange.");
      setAuthOpen(false);
      setForm({ display_name: "", email: "", password: "" });
      if (mode === "register") navigate("/onboarding");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[75] bg-forest/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAuthOpen(false)} data-testid="auth-modal">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-paper w-full max-w-4xl max-h-[92vh] overflow-auto hr-scroll grid md:grid-cols-2">
            <div className="hidden md:flex flex-col justify-between bg-forest text-bone p-8 grain relative">
              <div className="relative z-10">
                <div className="mb-6" data-testid="auth-logo"><Logo dark /></div>
                <span className="hr-overline text-saddle">Create your HomeRange</span>
                <h2 className="font-serif text-3xl mt-3 leading-tight">Browse free. Save when you&apos;re ready.</h2>
              </div>
              <ul className="relative z-10 space-y-2 text-sm text-bone/80 mt-6">
                {["My HomeRange profile", "Gear Locker", "Saved hunts & AI gear builds", "Personalized recommendations", "Private & published journals"].map((f) => (
                  <li key={f} className="before:content-['✓'] before:mr-2 before:text-saddle">{f}</li>
                ))}
              </ul>
            </div>

            <div className="p-8">
              <div className="md:hidden mb-5" data-testid="auth-logo-mobile"><Logo /></div>
              <div className="flex items-start justify-between">
                <div className="flex gap-1 bg-bone p-1">
                  <button onClick={() => setMode("register")} className={`px-4 py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-forest text-bone" : "text-charcoal/60"}`} data-testid="auth-tab-register">Sign up</button>
                  <button onClick={() => setMode("login")} className={`px-4 py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-forest text-bone" : "text-charcoal/60"}`} data-testid="auth-tab-login">Sign in</button>
                </div>
                <button onClick={() => setAuthOpen(false)} className="text-forest hover:text-blaze transition-colors" aria-label="Close" data-testid="auth-close"><X size={24} /></button>
              </div>

              <h3 className="font-serif text-3xl text-forest mt-6 mb-5">{mode === "login" ? "Welcome home." : "Join HomeRange."}</h3>
              <form onSubmit={submit} className="space-y-3">
                {mode === "register" && (
                  <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Name" className="w-full bg-white border border-saddle/25 px-4 py-3 outline-none focus:ring-2 focus:ring-blaze" data-testid="auth-name" />
                )}
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-white border border-saddle/25 px-4 py-3 outline-none focus:ring-2 focus:ring-blaze" data-testid="auth-email" />
                <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="w-full bg-white border border-saddle/25 px-4 py-3 outline-none focus:ring-2 focus:ring-blaze" data-testid="auth-password" />
                {err && <p className="text-destructive text-sm" data-testid="auth-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full bg-blaze text-white py-3.5 font-semibold hover:bg-blaze-dark transition-colors disabled:opacity-50" data-testid="auth-submit">
                  {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Free Account"}
                </button>
              </form>
              <p className="text-xs text-charcoal/45 mt-4">Try the demo hunter: <strong>demo@homerange.com</strong> / <strong>HomeRange123!</strong></p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
